/**
 * Main Parser for DFD Generation
 *
 * Coordinates AST parsing, analysis, and DFD building to extract
 * structured information from React and Vue component source code.
 */
import { SWCASTAnalyzer } from './ast-analyzer.js';
import { DefaultDFDBuilder } from './dfd-builder.js';
import { ParserErrorHandler } from '../utils/error-handler.js';
import { VueErrorHandler, VueAnalysisError } from '../utils/vue-error-handler.js';
import { SvelteErrorHandler, SvelteAnalysisError } from '../utils/svelte-error-handler.js';
import { VueSFCParseError } from './vue-sfc-parser.js';
// Export shared SFC parser for use by other frameworks (e.g., Svelte)
export { SFCParser, SFCParseError } from './sfc-parser.js';
/**
 * Default implementation of React Parser
 *
 * Orchestrates the parsing pipeline:
 * 1. Parse source code into AST using provided parser function
 * 2. Analyze AST to extract component information
 * 3. Build DFD source data from analysis
 * 4. Handle errors and timeouts gracefully
 */
export class DefaultReactParser {
    parserFn;
    astAnalyzer;
    dfdBuilder;
    errorHandler;
    constructor(parserFn, typeResolver) {
        this.parserFn = parserFn;
        this.astAnalyzer = new SWCASTAnalyzer(typeResolver);
        this.dfdBuilder = new DefaultDFDBuilder();
        this.errorHandler = new ParserErrorHandler();
    }
    /**
     * Parse React component source code and generate DFD source data
     *
     * @param sourceCode - The React component source code
     * @param filePath - The file path (used for error reporting and syntax detection)
     * @returns Promise resolving to DFDSourceData with nodes, edges, and any errors
     */
    async parse(sourceCode, filePath) {
        const context = {
            filePath,
            sourceCode,
            startTime: Date.now(),
        };
        try {
            // Wrap the entire parsing operation with timeout protection
            return await this.errorHandler.withTimeout(() => this.parseInternal(sourceCode, filePath, context), context);
        }
        catch (error) {
            // Handle timeout errors
            if (error instanceof Error && error.name === 'ParserTimeoutError') {
                return this.errorHandler.handleTimeout({}, context);
            }
            // Handle other unexpected errors
            const parseError = this.errorHandler.handleError(error instanceof Error ? error : new Error(String(error)), context);
            return this.errorHandler.createEmptyDFDData([parseError]);
        }
    }
    /**
     * Internal parsing implementation
     */
    async parseInternal(sourceCode, filePath, context) {
        const errors = [];
        // Step 1: Parse source code into AST
        const parseResult = await this.parserFn(sourceCode, filePath);
        if (parseResult.error) {
            // Handle syntax errors
            const syntaxError = this.errorHandler.handleSyntaxError(new Error(parseResult.error.message), context);
            return this.errorHandler.createEmptyDFDData([syntaxError]);
        }
        if (!parseResult.module) {
            // No module parsed
            const error = this.errorHandler.handleError(new Error('Failed to parse source code'), context);
            return this.errorHandler.createEmptyDFDData([error]);
        }
        // Check timeout after parsing
        if (this.errorHandler.isTimeoutExceeded(context.startTime)) {
            return this.errorHandler.handleTimeout({}, context);
        }
        // Step 2: Analyze AST to extract component information
        const analysis = await this.astAnalyzer.analyze(parseResult.module, filePath, sourceCode);
        if (!analysis) {
            // No React component found
            const componentNotFoundError = this.errorHandler.handleComponentNotFound(context);
            return this.errorHandler.createEmptyDFDData([componentNotFoundError]);
        }
        // Check timeout after analysis
        if (this.errorHandler.isTimeoutExceeded(context.startTime)) {
            return this.errorHandler.handleTimeout(analysis, context);
        }
        // Step 3: Build DFD source data from analysis
        const dfdData = this.dfdBuilder.build(analysis);
        // Add any accumulated errors
        if (errors.length > 0) {
            dfdData.errors = errors;
        }
        return dfdData;
    }
}
/**
 * Default implementation of Vue Parser
 *
 * Orchestrates the Vue parsing pipeline:
 * 1. Parse Vue SFC to extract script setup and template
 * 2. Parse script setup using SWC
 * 3. Analyze script and template to extract component information
 * 4. Build DFD source data from analysis
 * 5. Handle errors and timeouts gracefully
 */
export class DefaultVueParser {
    astAnalyzer; // VueASTAnalyzer - loaded dynamically
    parserFn;
    dfdBuilder;
    errorHandler;
    typeResolver;
    analyzerPromise;
    constructor(parserFn, typeResolver) {
        this.parserFn = parserFn;
        this.typeResolver = typeResolver;
        this.dfdBuilder = new DefaultDFDBuilder();
        this.errorHandler = new VueErrorHandler();
        // Load VueASTAnalyzer dynamically to avoid bundling @swc/core
        this.analyzerPromise = this.loadAnalyzer();
    }
    async loadAnalyzer() {
        const { VueASTAnalyzer } = await import('./vue-ast-analyzer.js');
        this.astAnalyzer = new VueASTAnalyzer(this.parserFn, this.typeResolver);
    }
    /**
     * Parse Vue component source code and generate DFD source data
     *
     * @param sourceCode - The Vue component source code
     * @param filePath - The file path (used for error reporting)
     * @returns Promise resolving to DFDSourceData with nodes, edges, and any errors
     */
    async parse(sourceCode, filePath) {
        const context = {
            filePath,
            sourceCode,
            startTime: Date.now(),
        };
        try {
            // Validate SFC structure first
            const validationErrors = this.errorHandler.validateSFCStructure(sourceCode, context);
            if (validationErrors.length > 0) {
                // Return empty DFD with validation errors
                return this.errorHandler.createEmptyDFDData(validationErrors);
            }
            // Wrap the entire parsing operation with timeout protection
            return await this.errorHandler.withTimeout(() => this.parseInternal(sourceCode, filePath, context), context);
        }
        catch (error) {
            // Handle Vue-specific errors
            if (error instanceof VueAnalysisError) {
                const parseError = {
                    message: error.getUserFriendlyMessage(),
                    line: error.line,
                    column: error.column,
                };
                return this.errorHandler.createEmptyDFDData([parseError]);
            }
            // Handle timeout errors
            if (error instanceof Error && error.name === 'VueAnalysisError' && error.code === 'TIMEOUT') {
                return this.errorHandler.handleTimeout(null, context);
            }
            // Handle SFC parse errors
            if (error instanceof VueSFCParseError) {
                const parseError = this.errorHandler.handleSFCError(error, context);
                return this.errorHandler.createEmptyDFDData([parseError]);
            }
            // Handle other unexpected errors
            const parseError = this.errorHandler.handleError(error instanceof Error ? error : new Error(String(error)), context);
            return this.errorHandler.createEmptyDFDData([parseError]);
        }
    }
    /**
     * Internal parsing implementation
     */
    async parseInternal(sourceCode, filePath, context) {
        const errors = [];
        let partialAnalysis = null;
        try {
            // Ensure analyzer is loaded
            await this.analyzerPromise;
            // Step 1: Analyze Vue SFC (parsing is done internally by VueASTAnalyzer)
            const analysis = await this.astAnalyzer.analyze(sourceCode, filePath);
            if (!analysis) {
                // No Vue component found
                const componentNotFoundError = this.errorHandler.handleMissingScriptSetup(context);
                return this.errorHandler.createEmptyDFDData([componentNotFoundError]);
            }
            partialAnalysis = analysis;
            // Check timeout after analysis
            if (this.errorHandler.isTimeoutExceeded(context.startTime)) {
                return this.errorHandler.handleTimeout(analysis, context);
            }
            // Step 2: Build DFD source data from analysis
            const dfdData = this.dfdBuilder.build(analysis);
            // Add any accumulated errors
            if (errors.length > 0) {
                dfdData.errors = errors;
            }
            return dfdData;
        }
        catch (error) {
            // Attempt partial analysis recovery
            const recoveredAnalysis = this.errorHandler.recoverPartialAnalysis(error instanceof Error ? error : new Error(String(error)), partialAnalysis, context);
            if (recoveredAnalysis) {
                // Build DFD from partial analysis
                const dfdData = this.dfdBuilder.build(recoveredAnalysis);
                // Add error information
                if (recoveredAnalysis.metadata?.error) {
                    dfdData.errors = [
                        {
                            message: recoveredAnalysis.metadata.error.message,
                            line: recoveredAnalysis.metadata.error.line,
                            column: recoveredAnalysis.metadata.error.column,
                        },
                    ];
                }
                return dfdData;
            }
            // No recovery possible, re-throw
            throw error;
        }
    }
}
/**
 * Default implementation of Svelte Parser
 *
 * Orchestrates the Svelte parsing pipeline:
 * 1. Parse Svelte SFC to extract script and markup
 * 2. Parse script using SWC
 * 3. Analyze script and markup to extract component information
 * 4. Build DFD source data from analysis
 * 5. Handle errors and timeouts gracefully
 */
export class DefaultSvelteParser {
    parserFn;
    astAnalyzer; // SvelteASTAnalyzer - loaded dynamically
    dfdBuilder;
    errorHandler;
    typeResolver;
    analyzerPromise;
    constructor(parserFn, typeResolver) {
        this.parserFn = parserFn;
        this.typeResolver = typeResolver;
        this.dfdBuilder = new DefaultDFDBuilder();
        this.errorHandler = new SvelteErrorHandler();
        // Load SvelteASTAnalyzer dynamically to avoid bundling @swc/core
        this.analyzerPromise = this.loadAnalyzer();
    }
    async loadAnalyzer() {
        const { SvelteASTAnalyzer } = await import('./svelte-ast-analyzer.js');
        this.astAnalyzer = new SvelteASTAnalyzer(this.parserFn, this.typeResolver);
    }
    /**
     * Parse Svelte component source code and generate DFD source data
     *
     * @param sourceCode - The Svelte component source code
     * @param filePath - The file path (used for error reporting)
     * @returns Promise resolving to DFDSourceData with nodes, edges, and any errors
     */
    async parse(sourceCode, filePath) {
        const context = {
            filePath,
            sourceCode,
            startTime: Date.now(),
        };
        try {
            // Validate SFC structure first
            const validationErrors = this.errorHandler.validateSFCStructure(sourceCode, context);
            if (validationErrors.length > 0) {
                // Return empty DFD with validation errors
                return this.errorHandler.createEmptyDFDData(validationErrors);
            }
            // Wrap the entire parsing operation with timeout protection
            return await this.errorHandler.withTimeout(() => this.parseInternal(sourceCode, filePath, context), context);
        }
        catch (error) {
            // Handle Svelte-specific errors
            if (error instanceof SvelteAnalysisError) {
                const parseError = error.toParseError();
                return this.errorHandler.createEmptyDFDData([parseError]);
            }
            // Handle timeout errors
            if (error instanceof Error && error.name === 'SvelteAnalysisError' && error.code === 'TIMEOUT') {
                return this.errorHandler.handleTimeout(null, context);
            }
            // Handle other unexpected errors
            const parseError = this.errorHandler.handleError(error instanceof Error ? error : new Error(String(error)), context);
            return this.errorHandler.createEmptyDFDData([parseError]);
        }
    }
    /**
     * Internal parsing implementation
     */
    async parseInternal(sourceCode, filePath, context) {
        const errors = [];
        let partialAnalysis = null;
        try {
            // Ensure analyzer is loaded before using it
            await this.analyzerPromise;
            // Step 1: Analyze Svelte SFC (parsing is done internally by SvelteASTAnalyzer)
            const analysis = await this.astAnalyzer.analyze(sourceCode, filePath);
            if (!analysis) {
                // No Svelte component found
                const componentNotFoundError = this.errorHandler.handleComponentNotFound(context);
                return this.errorHandler.createEmptyDFDData([componentNotFoundError]);
            }
            partialAnalysis = analysis;
            // Check timeout after analysis
            if (this.errorHandler.isTimeoutExceeded(context.startTime)) {
                return this.errorHandler.handleTimeout(analysis, context);
            }
            // Step 2: Build DFD source data from analysis
            const dfdData = this.dfdBuilder.build(analysis);
            // Add any accumulated errors
            if (errors.length > 0) {
                dfdData.errors = errors;
            }
            return dfdData;
        }
        catch (error) {
            // Attempt partial analysis recovery
            const recoveredAnalysis = this.errorHandler.recoverPartialAnalysis(error instanceof Error ? error : new Error(String(error)), partialAnalysis, context);
            if (recoveredAnalysis) {
                // Build DFD from partial analysis
                const dfdData = this.dfdBuilder.build(recoveredAnalysis);
                // Add error information
                if (recoveredAnalysis.metadata?.error) {
                    dfdData.errors = [recoveredAnalysis.metadata.error];
                }
                return dfdData;
            }
            // No recovery possible, re-throw
            throw error;
        }
    }
}
/**
 * Create a new React Parser instance
 *
 * @param parserFn - Parser function (Node or Browser implementation)
 * @param typeResolver - Optional TypeResolver for type information
 * @returns ReactParser instance
 */
export function createReactParser(parserFn, typeResolver) {
    return new DefaultReactParser(parserFn, typeResolver);
}
/**
 * Create a new Vue Parser instance
 *
 * @param typeResolver - Optional TypeResolver for type information
 * @returns ComponentParser instance
 */
export function createVueParser(parserFn, typeResolver) {
    return new DefaultVueParser(parserFn, typeResolver);
}
/**
 * Create a new Svelte Parser instance
 *
 * @param typeResolver - Optional TypeResolver for type information
 * @returns ComponentParser instance
 */
export function createSvelteParser(parserFn, typeResolver) {
    return new DefaultSvelteParser(parserFn, typeResolver);
}
/**
 * Create a parser based on file extension
 *
 * @param filePath - File path to determine parser type
 * @param parserFn - Parser function for React (Node or Browser implementation)
 * @param typeResolver - Optional TypeResolver for type information
 * @returns ComponentParser instance
 */
export function createParser(filePath, parserFn, typeResolver) {
    // Detect framework based on file extension
    if (filePath.endsWith('.vue')) {
        return createVueParser(parserFn, typeResolver);
    }
    if (filePath.endsWith('.svelte')) {
        return createSvelteParser(parserFn, typeResolver);
    }
    // Default to React parser for .tsx, .jsx, .ts, .js files
    return createReactParser(parserFn, typeResolver);
}
//# sourceMappingURL=index.js.map